import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import FeeGroupAgentToolbar from "./components/FeeGroupAgentToolbar";
import FeeGroupAgentSearch from "./components/FeeGroupAgentSearch";
import FeeGroupAgentList from "./components/FeeGroupAgentList";

type Filters = {
  name: string;
};

const initialFilters: Filters = {
  name: "",
};

const rowPerPage = 10;

const FeeGroupAgent = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const [showFilters, setShowFilters] = useState(false);

  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase() || "";

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      await fetchFeeGroups({ skip: 0, take: rowPerPage, ...initialFilters }, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const fetchFeeGroups = async (payload: any, signal?: AbortSignal, requestId?: number) => {
    try {
      const queryObject: any = {
        skip: payload?.offset || 0,
        take: payload?.take || rowPerPage,
      };

      if (payload?.name !== "" && payload?.name !== undefined) {
        queryObject.name = payload?.name;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/feegroup/all-feegroups?${queryString}&type=agent&owned_only=true`,
        { signal }
      );

      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      const newData = response.data?.feegroups?.payload || [];
      setTableData(newData);

      // Use pagination.total from API response
      const pagination = response.data?.feegroups?.pagination;
      if (pagination?.total !== undefined) {
        setRowCount(pagination.total);
      } else {
        // Fallback to heuristic if pagination.total not available
        if (newData.length === payload?.take) {
          setRowCount((payload?.skip / payload?.take + 1) * payload?.take + payload?.take);
        } else {
          setRowCount(payload?.skip + newData.length);
        }
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      console.error("Error fetching fee group:", error);
      setTableData([]);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    const newSkip = newValue.page * newValue.pageSize;
    setIsLoading(true);

    if (newValue.pageSize !== pageSize) {
      setPage(0);
      setPageSize(newValue.pageSize);
      await fetchFeeGroups({
        skip: 0,
        take: newValue.pageSize,
        ...filters,
      });
    } else {
      setPage(newValue.page);
      await fetchFeeGroups({
        skip: newSkip,
        take: newValue.pageSize,
        ...filters,
      });
    }
    setIsLoading(false);
  };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setPage(0);
    await fetchFeeGroups({ skip: 0, take: pageSize, ...filters });
    setIsLoading(false);
  };

  const handleSearchClear = async () => {
    setIsLoading(true);
    setFilters(initialFilters);
    setPage(0);
    await fetchFeeGroups({ skip: 0, take: pageSize, ...initialFilters });
    setIsLoading(false);
  };

  const handleClickCreate = () => {
    navigate(`/${userRole}/fee-groups/agent/create`);
  };

  const triggerRefresh = async () => {
    setIsLoading(true);
    const currentSkip = page * pageSize;
    await fetchFeeGroups({ skip: currentSkip, take: pageSize, ...filters });
    setIsLoading(false);
  };

  return (
    <>
      <FeeGroupAgentToolbar
        isLoading={isLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerRefresh}
        onNewFeeGroup={handleClickCreate}
      />
      <FeeGroupAgentSearch
        showFilters={showFilters}
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
      />
      <FeeGroupAgentList
        isLoading={isLoading}
        tableData={isLoading ? [] : tableData}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        setPaginationModel={setPaginationModel}
      />
    </>
  );
};

export default FeeGroupAgent;
